/**
 * Dependency-free tracking passthrough for Adelira shop links.
 * - Captures UTM/click IDs on first load, persists in sessionStorage + localStorage (30d).
 * - decorateShopUrl(url) appends stored params to any adelira.nl URL without duplicating keys.
 */

const STORAGE_KEY = "adelira_tracking";
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const TRACKED_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "fbclid",
  "gclid",
  "msclkid",
  "ttclid",
] as const;

type TrackingKey = (typeof TRACKED_KEYS)[number];
type TrackingData = Partial<Record<TrackingKey, string>>;

function readStore(): { data: TrackingData; savedAt: number } {
  // Prefer localStorage (survives tab close within 30d), fall back to sessionStorage
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.savedAt) {
        if (Date.now() - parsed.savedAt < EXPIRY_MS) {
          return { data: parsed.data || {}, savedAt: parsed.savedAt };
        }
      }
    }
  } catch {
    /* ignore */
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return { data: parsed.data || {}, savedAt: parsed.savedAt || Date.now() };
      }
    }
  } catch {
    /* ignore */
  }
  return { data: {}, savedAt: Date.now() };
}

function writeStore(data: TrackingData) {
  const payload = JSON.stringify({ data, savedAt: Date.now() });
  try {
    localStorage.setItem(STORAGE_KEY, payload);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, payload);
  } catch {
    /* ignore */
  }
}

export function captureTrackingParams() {
  if (typeof window === "undefined") return;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }

  const { data } = readStore();
  let changed = false;

  for (const key of TRACKED_KEYS) {
    const incoming = params.get(key);
    if (incoming && incoming.trim() !== "") {
      // Only overwrite if new non-empty value (never wipe with empty)
      if (data[key] !== incoming) {
        data[key] = incoming;
        changed = true;
      }
    }
  }

  if (changed) {
    writeStore(data);
  }
}

export function getStoredTracking(): TrackingData {
  return readStore().data;
}

/**
 * Append stored tracking params to any adelira.nl URL.
 * - Preserves existing params on the URL.
 * - Adds fallback utm_source=facebook&utm_medium=paid when fbclid exists but utm_source doesn't.
 * - No-op for non-adelira URLs.
 */
export function decorateShopUrl(url: string): string {
  if (!url) return url;
  let u: URL;
  try {
    u = new URL(url, typeof window !== "undefined" ? window.location.href : "https://huid-analyse.nl");
  } catch {
    return url;
  }

  if (!/(^|\.)adelira\.nl$/i.test(u.hostname)) return url;

  const stored = getStoredTracking();

  for (const key of TRACKED_KEYS) {
    const val = stored[key];
    if (val && !u.searchParams.has(key)) {
      u.searchParams.set(key, val);
    }
  }

  // Fallback: fbclid but no utm_source -> add facebook/paid
  const hasFbclid = u.searchParams.get("fbclid") || stored.fbclid;
  if (hasFbclid && !u.searchParams.get("utm_source")) {
    u.searchParams.set("utm_source", "facebook");
    if (!u.searchParams.get("utm_medium")) u.searchParams.set("utm_medium", "paid");
  }

  // Fallback: Instagram/Facebook in-app browser strips query params -> tag as Meta
  if (
    typeof navigator !== "undefined" &&
    /Instagram|FBAN|FBAV|FB_IAB/i.test(navigator.userAgent) &&
    !u.searchParams.get("utm_source")
  ) {
    u.searchParams.set("utm_source", "facebook");
    if (!u.searchParams.get("utm_medium")) u.searchParams.set("utm_medium", "paid_social");
  }


  return u.toString();
}
